import { z } from "zod";
import type { RuleSetMeta } from "../types.js";

// ---------------------------------------------------------------------------
// LMI — Lenders Mortgage Insurance: indicative cost, wait-vs-now break-even,
// and refund-on-early-discharge. Money Back Australia property-buyer suite.
//
// REGULATORY POSTURE (panel `lmi-cost-breakeven.md`, binding):
//  - LMI is *general insurance* (a s764A financial product) with NO carve-out,
//    BUT the borrower cannot shop it (the lender is the insured). So estimating
//    a non-shoppable lender-imposed cost is INFORMATION, not advice/dealing —
//    *provided* copy never steers toward an LMI-avoidance product/insurer.
//  - The premium grid has NO authoritative public statutory source and reprices
//    opaquely; the data-critic shows up to a ~2× spread between lender grids.
//    Therefore the premium SHIPS AS A STAMPED-INDICATIVE RANGE, never "your LMI
//    is $X". Encoding it as a definite quote is the estimate-as-quote misleading
//    -conduct edge (ACL s18 / ASIC-Act s12DB) — a kill-threat, not a style nit.
//  - The downstream mortgage is NCCP-regulated credit. A personalised result
//    routed to a paid broker/lender handoff is the C2 dealing-by-arranging kill
//    -line. `assertNoCreditHandoff` enforces this in code over every output.
//  - Refunds are being withdrawn insurer-by-insurer. Helia (historically the
//    largest insurer) has DISCONTINUED LMI refunds entirely. Telling a user
//    they are "owed $X" when their insurer no longer refunds is a currency
//    -coupled misrepresentation: the tool LEADS with disqualifiers and never
//    quotes a definite owed figure.
// ---------------------------------------------------------------------------

/** First Home Guarantee (5% Deposit Scheme) waives LMI when eligible. */
export const LMI_WAIVED = 0 as const;

// ===========================================================================
// lmiCost — indicative premium by LVR band + loan-size band
// ===========================================================================

export const LmiCostInputSchema = z
  .object({
    /** Property value (purchase price / valuation), AUD. */
    propertyValue: z.number().positive(),
    /**
     * Loan amount, AUD. Exactly one of `loanAmount` / `deposit` must be given;
     * we derive LVR = loanAmount / propertyValue.
     */
    loanAmount: z.number().positive().optional(),
    /** Deposit, AUD (loanAmount = propertyValue − deposit). */
    deposit: z.number().min(0).optional(),
    /**
     * Eligible for the First Home Guarantee / 5% Deposit Scheme. When true the
     * Commonwealth guarantees the deposit gap and the lender requires no LMI →
     * indicative premium is $0 (the tool's key differentiator).
     */
    firstHomeGuaranteeEligible: z.boolean().default(false),
    /**
     * Period being assessed, e.g. "2025-26". Passed in (never derived from the
     * clock) so historical re-runs resolve against the grid in force then.
     */
    assessmentYear: z.string(),
  })
  .refine(
    (v) => v.loanAmount !== undefined || v.deposit !== undefined,
    "supply either loanAmount or deposit",
  );

// `z.input` (not `z.infer`/`z.output`) so defaulted fields stay OPTIONAL on the
// caller's side — the schema fills them at parse time.
export type LmiCostInput = z.input<typeof LmiCostInputSchema>;

/**
 * One LVR tier of the indicative grid. The premium within a tier is expressed
 * as a RANGE (`ratePctLow`–`ratePctHigh` of the loan), reflecting the genuine
 * ~2× inter-lender / loan-size spread. We never collapse it to a point — a
 * point estimate read as a quote is the misleading-conduct edge.
 */
export interface LvrTier {
  /** Lower bound of LVR for this tier, inclusive (fraction, e.g. 0.90). */
  fromLvr: number;
  /** Upper bound, exclusive. `null` = unbounded top tier (e.g. 95%+). */
  toLvr: number | null;
  /** Low end of the indicative premium rate (fraction of loan). 0 below 80%. */
  ratePctLow: number;
  /** High end of the indicative premium rate (fraction of loan). */
  ratePctHigh: number;
  /** Human label, e.g. "90–95% LVR". */
  label: string;
}

export interface LmiCostRuleSet extends RuleSetMeta {
  /** LVR below which no LMI is charged (the 80% cliff). */
  lmiThresholdLvr: number;
  /**
   * Indicative premium grid by LVR tier. Authored to cover [0, 1.0] LVR. Every
   * rate is INDICATIVE (no public statutory source) — see module header.
   */
  tiers: readonly LvrTier[];
}

/** An indicative dollar range. Never a single "your fee" number. */
export interface IndicativeRange {
  low: number;
  high: number;
}

export interface LmiCostOutput {
  /** LVR the calc ran against (loanAmount / propertyValue). */
  lvr: number;
  loanAmount: number;
  /** Whether LMI applies at all (LVR above the 80% threshold). */
  lmiApplies: boolean;
  /** Whether the FHBG waiver zeroed the premium. */
  waivedByFirstHomeGuarantee: boolean;
  /**
   * INDICATIVE premium range, AUD. `{low:0,high:0}` when waived or below the
   * threshold. NEVER a definite quote — the UI must render it as a range with
   * the "indicative, your lender's actual LMI may differ" stamp.
   */
  indicativePremium: IndicativeRange;
  /** The LVR tier label, e.g. "90–95% LVR", or "Below 80% LVR". */
  lvrBand: string;
  /**
   * The "one tier down" framing: the LVR threshold of the next-lower tier and
   * the extra deposit needed to reach it (which drops you below the next cliff).
   * `null` when already below the LMI threshold.
   */
  nextTierDown: {
    /** LVR at which the lower (cheaper) tier begins. */
    atLvr: number;
    /** Extra deposit (AUD) needed to reach that LVR. */
    extraDeposit: number;
  } | null;
  /** Hard guarantee for callers/UI: this is an estimate, not a quote. */
  isIndicativeEstimate: true;
}

// ===========================================================================
// payNowVsSaveTo20 — wait-vs-now break-even as a SENSITIVITY BAND
// ===========================================================================

export const PayNowVsSaveInputSchema = z.object({
  /** Property value today, AUD. */
  propertyValue: z.number().positive(),
  /** Deposit saved today, AUD (drives today's LVR and today's LMI). */
  deposit: z.number().min(0),
  /** How much the user can add to the deposit each year, AUD/yr. */
  annualSavingsRate: z.number().min(0),
  /**
   * Labelled USER ASSUMPTIONS for annual property price movement (fractions,
   * e.g. [-0.02, 0, 0.05]). These are the user's own what-ifs — NEVER a
   * forecast we supply. Defaulted to a -2% / 0% / +5% sensitivity band.
   */
  priceMovementScenarios: z
    .array(z.number())
    .nonempty()
    .default([-0.02, 0, 0.05]),
  assessmentYear: z.string(),
});

export type PayNowVsSaveInput = z.input<typeof PayNowVsSaveInputSchema>;

/** One row of the wait-to-20% sensitivity band, per price-movement assumption. */
export interface SaveTo20Scenario {
  /** The labelled price-movement assumption this row uses (fraction/yr). */
  priceMovementAssumption: number;
  /**
   * Years of saving to reach a 20% deposit under this assumption, or `null` if
   * the deposit never catches a rising target (savings rate too low).
   */
  yearsToReach20: number | null;
  /** Property value at that point, AUD (today's value grown by the assumption). */
  projectedPropertyValue: number | null;
}

export interface PayNowVsSaveOutput {
  /** Today's LVR (loan / value), to show whether LMI is payable now. */
  lvrNow: number;
  /** Indicative LMI if buying now (range — see lmiCost). */
  indicativePremiumNow: IndicativeRange;
  /** Already at/under 20% deposit today → no wait, no LMI. */
  alreadyAt20: boolean;
  /**
   * The sensitivity band: one row per supplied price-movement assumption. The
   * tool presents THIS, never a single "buy now and you're $X ahead" figure —
   * the verdict flips with the assumption, so a point answer is a disguised
   * forecast (misleading-conduct edge).
   */
  scenarios: readonly SaveTo20Scenario[];
  /** Hard guarantee: this is a conditional band, not a recommendation. */
  isSensitivityBand: true;
}

// ===========================================================================
// refundOnDischarge — partial-refund scale, DISQUALIFIER-LED
// ===========================================================================

/** Known insurer refund posture. Helia = discontinued (the core decay fact). */
export const InsurerSchema = z.enum(["helia", "qbe", "arch", "unknown"]);
export type Insurer = z.infer<typeof InsurerSchema>;

export const RefundInputSchema = z.object({
  /**
   * The LMI insurer, if known. `helia` → refunds DISCONTINUED (lead with the
   * disqualifier). `unknown` → we cannot assert any entitlement.
   */
  insurer: InsurerSchema.default("unknown"),
  /** The original LMI premium paid, AUD. Used ONLY for an indicative range. */
  premiumPaid: z.number().min(0),
  /** Months since the premium was paid (drives the partial-refund tier). */
  monthsSincePremium: z.number().min(0),
  /**
   * Whether ALL loans secured by the property are repaid in full and the
   * mortgage fully discharged. Partial discharge disqualifies (QBE primary).
   */
  fullyDischarged: z.boolean().default(false),
  /**
   * Whether the lender was (or can be) advised within the insurer's notice
   * window (~30 days). Missing the window disqualifies.
   */
  lenderNotifiedWithinWindow: z.boolean().default(false),
  assessmentYear: z.string(),
});

export type RefundInput = z.input<typeof RefundInputSchema>;

/** A refund-tier on the discharge scale (insurer/lender-specific, declining). */
export interface RefundTier {
  /** Lower bound of months-since-premium, inclusive. */
  fromMonths: number;
  /** Upper bound, exclusive. `null` = unbounded (no refund beyond here). */
  toMonths: number | null;
  /** Indicative low fraction of premium refunded in this tier. */
  refundPctLow: number;
  /** Indicative high fraction of premium refunded in this tier. */
  refundPctHigh: number;
  label: string;
}

export interface RefundOnDischargeRuleSet extends RuleSetMeta {
  /** Insurers that have DISCONTINUED refunds entirely (lead disqualifier). */
  refundsDiscontinuedInsurers: readonly Insurer[];
  /** Insurer notice window in days (miss it → disqualified). */
  lenderNoticeWindowDays: number;
  /** The indicative partial-refund scale. Authored to cover [0, ∞) months. */
  tiers: readonly RefundTier[];
  /**
   * Indicative deduction applied to a refund for non-refundable stamp duty on
   * the premium (the refund is "minus stamp duty"). Fraction of premium.
   */
  stampDutyDeductionPctIndicative: number;
}

export interface RefundOnDischargeOutput {
  /**
   * Leading disqualifiers — the reasons NO refund is payable. Non-empty means
   * the user is NOT owed anything; the UI must lead with these and must NOT
   * show a dollar figure. Drives the "never quote you're owed $X" rule.
   */
  disqualifiers: readonly string[];
  /** True iff every gate passed AND the insurer still offers refunds. */
  potentiallyEligible: boolean;
  /** True iff the insurer is on the discontinued list (Helia). */
  insurerDiscontinuedRefunds: boolean;
  /**
   * INDICATIVE possible-refund range, AUD, ONLY when `potentiallyEligible`.
   * `null` whenever disqualified — we never compute a number we'd have to
   * caveat away. Even when present it is "possible, confirm with your lender",
   * never "you are owed $X".
   */
  indicativeRefundRange: IndicativeRange | null;
  /** The refund tier label (e.g. "≤12 months"), or null if no tier applies. */
  tierLabel: string | null;
  /** Hard guarantee: never a definite entitlement. */
  isIndicativePossibleRefund: true;
}
